/**
 * Card de imóvel reutilizável (Grid View)
 * Extraído de PropertiesContent.tsx para reuso em Interesses de Leads
 */

import React from 'react';
import { IoBedOutline, IoCarOutline } from 'react-icons/io5';
import { LuBath, LuRuler } from 'react-icons/lu';
import type { DatabasePropertyData, DatabaseDocumentResponse, BusinessOpportunity } from '@troia-v3/types';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons'
type PropertyDocument = DatabaseDocumentResponse<DatabasePropertyData>;

export interface PropertyCardProps {
  property: PropertyDocument;
  onClick?: () => void;
  /** Use CSS variables (page context) or hardcoded light colors (modal context) */
  alwaysLight?: boolean;
  /** Menu ••• de ações do card (F39: paridade com Documentos). Omitido = sem kebab. */
  actions?: CardAction[];
}

/**
 * Formata valor monetário de forma compacta
 */
export const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}mil`;
  }
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const PropertyCard: React.FC<PropertyCardProps> = ({ property, onClick, alwaysLight, actions }) => {
  const propertyData = property.data;
  const firstPhoto = propertyData?.media?.photos?.[0];
  const saleOpportunity = propertyData?.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'sale');
  const rentOpportunity = propertyData?.opportunities?.find((opp: BusinessOpportunity) => opp.type === 'rent');

  const bedrooms = propertyData?.features?.bedrooms;
  const bathrooms = propertyData?.features?.bathrooms;
  const parkingSpaces = propertyData?.features?.parkingSpaces;
  const area = propertyData?.features?.totalArea || propertyData?.features?.privateArea;

  const cardBg = alwaysLight ? '#ffffff' : 'var(--theme-card-background)';
  const cardBorder = alwaysLight ? '#e5e7eb' : 'var(--theme-card-border)';
  const cardText = alwaysLight ? '#1e293b' : 'var(--theme-card-text)';
  const mutedText = alwaysLight ? '#64748b' : 'var(--theme-muted-text)';
  const mutedBg = alwaysLight ? '#f1f5f9' : 'var(--theme-muted-background, #f1f5f9)';

  return (
    <div
      className="rounded-lg border overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group"
      style={{ backgroundColor: cardBg, borderColor: cardBorder }}
      onClick={onClick}
      data-testid={`property-card-${property.id}`}
    >
      {/* Imagem com Badges de Preço */}
      <div className="relative aspect-[4/3] overflow-hidden">
        {firstPhoto ? (
          <img
            src={firstPhoto}
            alt={propertyData?.title || 'Imóvel'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: mutedBg }}
          >
            <Icon name="home" className="w-10 h-10" style={{ color: mutedText }} />
          </div>
        )}

        {/* Badges de Preço - Overlay */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {saleOpportunity?.pricing?.amount && (
            <div
              className="px-2 py-1 rounded text-xs font-semibold shadow-sm"
              style={{ backgroundColor: alwaysLight ? '#10B981' : 'var(--theme-success-color)', color: '#ffffff' }}
            >
              {formatPrice(saleOpportunity.pricing.amount)}
            </div>
          )}
          {rentOpportunity?.pricing?.amount && (
            <div
              className="px-2 py-1 rounded text-xs font-semibold shadow-sm"
              style={{ backgroundColor: alwaysLight ? '#8b5cf6' : 'var(--theme-primary)', color: '#ffffff' }}
            >
              {formatPrice(rentOpportunity.pricing.amount)}/mês
            </div>
          )}
        </div>

        {/* Badge de Tipo de Imóvel */}
        {propertyData?.propertyType && (
          <div
            className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium shadow-sm"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', color: '#ffffff' }}
          >
            {propertyData.propertyType === 'apartment' && 'Apartamento'}
            {propertyData.propertyType === 'house' && 'Casa'}
            {propertyData.propertyType === 'land' && 'Terreno'}
            {propertyData.propertyType === 'commercial' && 'Comercial'}
            {propertyData.propertyType === 'farm' && 'Fazenda'}
            {propertyData.propertyType === 'warehouse' && 'Galpão'}
            {propertyData.propertyType === 'office' && 'Escritório'}
            {propertyData.propertyType === 'penthouse' && 'Cobertura'}
            {propertyData.propertyType === 'studio' && 'Studio'}
            {propertyData.propertyType === 'townhouse' && 'Sobrado'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <div className="flex items-start gap-1.5 mb-1.5">
          <h3
            className="flex-1 min-w-0 font-semibold text-sm line-clamp-1"
            style={{ color: cardText }}
          >
            {propertyData?.title || 'Sem título'}
          </h3>
          {actions && actions.length > 0 && (
            <div className="flex-shrink-0 -mr-1 -mt-1" onClick={(e) => e.stopPropagation()}>
              <CardActionsMenu actions={actions} size="sm" data-testid={`property-card-actions-${property.id}`} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 mb-2.5">
          <Icon name="map-pin" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: mutedText }} />
          <p className="text-xs line-clamp-1" style={{ color: mutedText }}>
            {propertyData?.address?.neighborhood && propertyData?.address?.city
              ? `${propertyData.address.neighborhood}, ${propertyData.address.city}`
              : propertyData?.address?.city || 'Local não informado'}
          </p>
        </div>

        <div
          className="flex items-center gap-3 pt-2.5 border-t"
          style={{ borderColor: cardBorder }}
        >
          {bedrooms != null && bedrooms > 0 && (
            <div className="flex items-center gap-1" title="Quartos">
              <IoBedOutline className="w-4 h-4" style={{ color: mutedText }} />
              <span className="text-xs font-medium" style={{ color: cardText }}>{bedrooms}</span>
            </div>
          )}
          {bathrooms != null && bathrooms > 0 && (
            <div className="flex items-center gap-1" title="Banheiros">
              <LuBath className="w-4 h-4" style={{ color: mutedText }} />
              <span className="text-xs font-medium" style={{ color: cardText }}>{bathrooms}</span>
            </div>
          )}
          {parkingSpaces != null && parkingSpaces > 0 && (
            <div className="flex items-center gap-1" title="Vagas">
              <IoCarOutline className="w-4 h-4" style={{ color: mutedText }} />
              <span className="text-xs font-medium" style={{ color: cardText }}>{parkingSpaces}</span>
            </div>
          )}
          {area != null && area > 0 && (
            <div className="flex items-center gap-1" title="Área">
              <LuRuler className="w-4 h-4" style={{ color: mutedText }} />
              <span className="text-xs font-medium" style={{ color: cardText }}>{area}m²</span>
            </div>
          )}
          {(!bedrooms || bedrooms === 0) && (!bathrooms || bathrooms === 0) && (!parkingSpaces || parkingSpaces === 0) && (!area || area === 0) && (
            <span className="text-xs" style={{ color: mutedText }}>
              Características não informadas
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
